import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns/formatDistanceToNow';
import { FileSpreadsheet, Layers, PenLine, Table2, ArrowRight } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { sourceMeta } from './sourceMeta';
import { containerVariants, itemVariants } from './motion';
import type { Product } from '../mockKnowledge';

interface ProductSourcesGridProps {
  products: Product[];
  onAddManual: () => void;
  onImportCsv: () => void;
  onConnectSheet: () => void;
}

export function ProductSourcesGrid({ products, onAddManual, onImportCsv, onConnectSheet }: ProductSourcesGridProps) {
  const shopifyMeta = sourceMeta.shopify;
  const woocommerceMeta = sourceMeta.woocommerce;
  const shopifyProducts = products.filter((product) => product.source === 'shopify');
  const shopifyLastSynced = shopifyProducts.reduce<string | undefined>((latest, product) => {
    if (!product.lastSynced) return latest;
    if (!latest || product.lastSynced > latest) return product.lastSynced;
    return latest;
  }, undefined);

  return (
    <Card className="max-w-none overflow-hidden rounded-2xl border border-slate-200/90 dark:border-slate-800/90 bg-slate-50/40 dark:bg-slate-900/60 shadow-xs">
      {/* Pod Header */}
      <div className="flex flex-wrap items-center justify-between border-b border-slate-200/70 dark:border-slate-800/70 bg-slate-100/50 dark:bg-slate-900/80 px-4 py-2.5 gap-2">
        <div className="flex items-center gap-2">
          <span className="flex h-5 w-5 items-center justify-center rounded-lg bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
            <Layers className="h-3 w-3" />
          </span>
          <span className="text-[11px] font-bold tracking-wider uppercase text-slate-800 dark:text-slate-200">
            Catalog & Ingestion Channels
          </span>
        </div>
        <span className="text-[10px] font-medium text-slate-400 dark:text-slate-500">5 sources available</span>
      </div>

      <div className="p-3 bg-white/70 dark:bg-slate-950/40">
        <motion.div
          className="grid grid-cols-1 gap-2.5 sm:grid-cols-2 lg:grid-cols-3"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Shopify */}
          <motion.div variants={itemVariants}>
            <div className="flex h-full items-center justify-between gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={cn('flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl', shopifyMeta.iconBg)}>
                  <shopifyMeta.icon className={cn('h-4 w-4', shopifyMeta.iconText)} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-1.5">
                    <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white truncate">Shopify</h3>
                    {shopifyProducts.length > 0 && (
                      <span className="inline-flex items-center gap-1 rounded-full border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/50 px-1.5 py-0.2 text-[8px] font-bold text-emerald-700 dark:text-emerald-400">
                        <span className="h-1 w-1 rounded-full bg-emerald-500 animate-pulse" />
                        Synced
                      </span>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 dark:text-slate-400 truncate">
                    {shopifyProducts.length > 0 ? `${shopifyProducts.length} items synced` : 'Online Store'}
                  </p>
                </div>
              </div>
              <div className="shrink-0">
                <Button asChild size="sm" variant="outline" className="h-7 px-3 rounded-full border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-[11px] font-semibold text-slate-700 dark:text-slate-200 transition-colors shadow-2xs">
                  <Link to="/integrations">Manage</Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* WooCommerce */}
          <motion.div variants={itemVariants}>
            <div className="flex h-full items-center justify-between gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className={cn('flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl', woocommerceMeta.iconBg)}>
                  <woocommerceMeta.icon className={cn('h-4 w-4', woocommerceMeta.iconText)} />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white truncate">WooCommerce</h3>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">WordPress REST API</p>
                </div>
              </div>
              <div className="shrink-0">
                <Button asChild size="sm" variant="outline" className="h-7 px-3 rounded-full border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-[11px] font-semibold text-slate-700 dark:text-slate-200 transition-colors shadow-2xs">
                  <Link to="/integrations">Connect</Link>
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Add Manually */}
          <motion.div variants={itemVariants}>
            <div className="flex h-full items-center justify-between gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl bg-emerald-500/10 dark:bg-emerald-500/20 text-emerald-600 dark:text-emerald-400">
                  <PenLine className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white truncate">Manual Entry</h3>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">Single product item</p>
                </div>
              </div>
              <div className="shrink-0">
                <Button size="sm" onClick={onAddManual} className="h-7 px-3 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-[11px] font-semibold shadow-xs transition-colors">
                  Add Item
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Import CSV */}
          <motion.div variants={itemVariants}>
            <div className="flex h-full items-center justify-between gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl bg-indigo-500/10 dark:bg-indigo-500/20 text-indigo-600 dark:text-indigo-400">
                  <FileSpreadsheet className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white truncate">Import CSV</h3>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">Excel / CSV batch</p>
                </div>
              </div>
              <div className="shrink-0">
                <Button size="sm" variant="outline" onClick={onImportCsv} className="h-7 px-3 rounded-full border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-[11px] font-semibold text-slate-700 dark:text-slate-200 transition-colors shadow-2xs">
                  Upload CSV
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Google Sheet */}
          <motion.div variants={itemVariants}>
            <div className="flex h-full items-center justify-between gap-2.5 rounded-xl border border-slate-200/80 dark:border-slate-800 bg-white dark:bg-slate-900 p-3 shadow-2xs transition-all hover:border-slate-300 dark:hover:border-slate-700">
              <div className="flex items-center gap-2.5 min-w-0">
                <div className="flex h-8.5 w-8.5 shrink-0 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400">
                  <Table2 className="h-4 w-4" />
                </div>
                <div className="min-w-0">
                  <h3 className="text-xs font-bold uppercase tracking-wider text-slate-900 dark:text-white truncate">Google Sheet</h3>
                  <p className="text-[10px] text-slate-400 dark:text-slate-500 truncate">Live Sheet sync</p>
                </div>
              </div>
              <div className="shrink-0">
                <Button size="sm" variant="outline" onClick={onConnectSheet} className="h-7 px-3 rounded-full border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/80 hover:bg-white dark:hover:bg-slate-700 text-[11px] font-semibold text-slate-700 dark:text-slate-200 transition-colors shadow-2xs">
                  Sync Sheet
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </Card>
  );
}

export default ProductSourcesGrid;
